import { useState } from 'react'
import { createDebtPayment } from '~/billing/services/billingService'
import { Button } from '~/components/common/Button'
import { Typography } from '~/components/common/Typography'
import { useOptionalBillingContext } from '~/contexts/BillingContext'

export interface AccountLockedViewProps {
  /** The amount of credit debt that caused the lock */
  creditDebt: number
  /** Minimum credits required to unlock the account */
  minimumCreditsToUnlock: number
  /** Reason for the lock */
  lockReason?: string | null
  /** Override for billing admin status (for testing) */
  isBillingAdminOverride?: boolean
}

/**
 * Full-screen view shown when an account is locked due to credit debt.
 *
 * This is displayed when a user's account has exceeded their credit limit
 * and has accumulated credit debt. The view blocks access to the application
 * until the debt is paid.
 *
 * - Billing admins see a button to pay debt and unlock the account
 * - Non-admin users see a message to contact their billing administrator
 */
export function AccountLockedView({
  creditDebt,
  minimumCreditsToUnlock,
  lockReason,
  isBillingAdminOverride
}: AccountLockedViewProps) {
  const billingContext = useOptionalBillingContext()
  const isBillingAdmin = isBillingAdminOverride ?? billingContext?.isBillingAdmin ?? false
  const billingId = billingContext?.billingInfo?.id
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handlePayDebt = async () => {
    if (!billingId) {
      setError('Billing account not found')
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const response = await createDebtPayment(
        billingId,
        `${window.location.origin}${window.location.pathname}?payment=success`,
        `${window.location.origin}${window.location.pathname}?payment=cancelled`
      )

      // Redirect to Stripe checkout
      window.location.href = response.checkout_url
    } catch (err) {
      console.error('Failed to create debt payment:', err)
      setError(err instanceof Error ? err.message : 'Failed to create payment')
      setIsLoading(false)
    }
  }

  return (
    <div className='flex flex-col items-center justify-center h-full w-full p-8 gap-6 bg-tokens-neutral-bg-primary'>
      <div className='flex flex-col items-center gap-4 max-w-lg text-center'>
        {/* Lock Icon */}
        <div className='w-20 h-20 rounded-full bg-tokens-status-error-10 flex items-center justify-center'>
          <svg
            width='40'
            height='40'
            viewBox='0 0 24 24'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
            className='text-tokens-status-error-1'
          >
            <path
              d='M17 11V8C17 5.23858 14.7614 3 12 3C9.23858 3 7 5.23858 7 8V11M12 14V17M8.8 21H15.2C16.8802 21 17.7202 21 18.362 20.673C18.9265 20.3854 19.3854 19.9265 19.673 19.362C20 18.7202 20 17.8802 20 16.2V15.8C20 14.1198 20 13.2798 19.673 12.638C19.3854 12.0735 18.9265 11.6146 18.362 11.327C17.7202 11 16.8802 11 15.2 11H8.8C7.11984 11 6.27976 11 5.63803 11.327C5.07354 11.6146 4.6146 12.0735 4.32698 12.638C4 13.2798 4 14.1198 4 15.8V16.2C4 17.8802 4 18.7202 4.32698 19.362C4.6146 19.9265 5.07354 20.3854 5.63803 20.673C6.27976 21 7.11984 21 8.8 21Z'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        </div>

        {/* Title */}
        <Typography
          fontFamily='geist'
          fontSize='headlineMd'
          className='text-tokens-neutral-text-primary'
        >
          Account Locked
        </Typography>

        {/* Description */}
        <Typography
          fontFamily='geist'
          fontSize='bodyMd'
          className='text-tokens-neutral-text-secondary'
        >
          Your account has been locked due to unpaid credit debt. You cannot access workspaces or
          run tasks until the debt is paid.
        </Typography>

        {/* Debt Info Card */}
        <div className='w-full mt-2 p-4 rounded-xl bg-tokens-neutral-bg-secondary border border-tokens-neutral-border-1'>
          <div className='flex flex-col gap-3'>
            <div className='flex justify-between items-center'>
              <Typography
                fontFamily='geist'
                fontSize='bodySm'
                className='text-tokens-neutral-text-secondary'
              >
                Credit Debt
              </Typography>
              <Typography
                fontFamily='geist'
                fontSize='bodyMdMedium'
                className='text-tokens-status-error-1'
              >
                {creditDebt} credits
              </Typography>
            </div>
            <div className='flex justify-between items-center'>
              <Typography
                fontFamily='geist'
                fontSize='bodySm'
                className='text-tokens-neutral-text-secondary'
              >
                Minimum to Unlock
              </Typography>
              <Typography
                fontFamily='geist'
                fontSize='bodyMdMedium'
                className='text-tokens-neutral-text-primary'
              >
                {minimumCreditsToUnlock} credits
              </Typography>
            </div>
          </div>
        </div>

        {/* Conditional messaging based on admin status */}
        {isBillingAdmin ? (
          <div className='flex flex-col items-center gap-3 mt-2'>
            <Typography
              fontFamily='geist'
              fontSize='bodySm'
              className='text-tokens-neutral-text-tertiary'
            >
              Pay your debt of {minimumCreditsToUnlock} credits to unlock your account.
            </Typography>
            {error && (
              <Typography
                fontFamily='geist'
                fontSize='bodySm'
                className='text-tokens-status-error-1'
              >
                {error}
              </Typography>
            )}
            <Button variant='primary' onClick={handlePayDebt} isDisabled={isLoading}>
              {isLoading ? 'Processing...' : 'Pay Debt & Unlock'}
            </Button>
          </div>
        ) : (
          <div className='flex flex-col items-center gap-2 mt-2'>
            <Typography
              fontFamily='geist'
              fontSize='bodySm'
              className='text-tokens-neutral-text-tertiary'
            >
              Please contact your billing administrator to resolve this issue.
            </Typography>
          </div>
        )}

        {/* Lock reason if provided */}
        {lockReason && (
          <Typography
            fontFamily='geist'
            fontSize='bodySm'
            className='text-tokens-neutral-text-tertiary mt-2'
          >
            Reason: {lockReason}
          </Typography>
        )}
      </div>
    </div>
  )
}

export default AccountLockedView
